import {
  NumberFilterOperator,
  NumberValue,
} from "@redotech/redo-model/views/advanced-filters/number-filter";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import * as classnames from "classnames";
import { memo, useState } from "react";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
} from "../../arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "../../arbiter-components/list/redo-list";
import { RedoListItemSize } from "../../arbiter-components/list/redo-list-item";
import { RedoSingleSelectDropdown } from "../../arbiter-components/select-dropdown/redo-single-select-dropdown";
import ChevronDown from "../../arbiter-icon/chevron-down_filled.svg";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { variableToSemanticDisplay } from "../../utils/display-code-variables";
import { NumberTableFilter } from "../advanced-filter";
import * as styles from "./filter.module.css";

export const NumberFilterGroup = memo(function NumberFilterGroup({
  filter,
  setFilter,
  removeFilter,
}: {
  filter: NumberTableFilter;
  setFilter(filter: NumberTableFilter): void;
  removeFilter(): void;
}) {
  const [operatorRef, setOperatorRef] = useState<HTMLButtonElement | null>(
    null,
  );

  const { name, operator, value } = filter.data;

  const operatorAnchor = (
    <RedoFilterDropdownAnchor
      color="secondary"
      IconTrailing={ChevronDown}
      ref={setOperatorRef}
      text={numberFilterOperatorTypeToText[operator]}
    />
  );

  const operatorOptions: RedoListItem<NumberFilterOperator>[] = Object.values(
    NumberFilterOperator,
  ).map((item) => {
    return { value: item };
  });

  const operatorDropdown = (
    <RedoSingleSelectDropdown
      dropdownButtonRef={operatorRef}
      options={operatorOptions}
      optionSelected={(operator: RedoListItem<NumberFilterOperator>) => {
        setFilter({
          ...filter,
          data: {
            ...filter.data,
            operator: operator.value,
            value:
              operator.value === NumberFilterOperator.BETWEEN
                ? [value?.[0] ?? 0, value?.[1] ?? 1]
                : [value?.[0] ?? 0],
          },
        });
      }}
      selectedItem={{ value: operator }}
      size={RedoListItemSize.SMALL}
    >
      {(item) => (
        <Text
          fontSize="sm"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {numberFilterOperatorTypeToText[item.value]}
        </Text>
      )}
    </RedoSingleSelectDropdown>
  );

  const step = filter?.step ?? 1;
  const min = filter?.min ?? 0;

  const valueAnchor = (
    <Flex>
      <RedoIncrementDecrement
        className={classnames(
          styles.filterIncDec,
          operator === NumberFilterOperator.BETWEEN
            ? styles.rightBorder
            : undefined,
        )}
        max={filter.max ?? undefined}
        min={filter.min ?? undefined}
        placeholder={
          min >= 1 || step >= 1
            ? min.toString()
            : min.toFixed(step.toString().split(".")[1]?.length || 0)
        }
        prefix={filter?.prefix}
        setValue={(newNumber) => {
          let newVal: NumberValue = [newNumber];
          if (operator === NumberFilterOperator.BETWEEN) {
            newVal = [newNumber, value?.[1] ?? newNumber + 1];
          }
          setFilter({ ...filter, data: { ...filter.data, value: newVal } });
        }}
        size="small"
        step={filter?.step ?? undefined}
        suffix={filter?.suffix}
        value={value?.[0] ?? 0}
      />
      {operator === NumberFilterOperator.BETWEEN && (
        <Flex>
          <Text fontSize="sm" textColor="secondary">
            and
          </Text>
          <RedoIncrementDecrement
            className={classnames(
              styles.filterIncDec,
              operator === NumberFilterOperator.BETWEEN
                ? styles.leftBorder
                : undefined,
            )}
            max={filter.max ?? undefined}
            min={filter.min ?? undefined}
            placeholder={
              min >= 1 || step >= 1
                ? min.toString()
                : min.toFixed(step.toString().split(".")[1]?.length || 0)
            }
            prefix={filter?.prefix}
            setValue={(newNumber) => {
              let newVal: NumberValue = [0, newNumber];
              if (value?.[0]) {
                newVal = [value[0], newNumber];
              }
              setFilter({ ...filter, data: { ...filter.data, value: newVal } });
            }}
            size="small"
            step={filter?.step ?? undefined}
            suffix={filter?.suffix}
            value={value?.[1] ?? (value?.[0] ?? 0) + 1}
          />
        </Flex>
      )}
    </Flex>
  );

  return (
    <>
      {operatorDropdown}
      <RedoFilterGroup
        Icon={filter.Icon}
        propertyName={variableToSemanticDisplay(name) ?? name}
        query={operatorAnchor}
        removeFilter={removeFilter}
        value={valueAnchor}
      />
    </>
  );
});

export const numberFilterOperatorTypeToText: Record<
  NumberFilterOperator,
  string
> = {
  [NumberFilterOperator.EQUAL]: "equals",
  [NumberFilterOperator.LESS_THAN]: "less than",
  [NumberFilterOperator.GREATER_THAN]: "greater than",
  [NumberFilterOperator.BETWEEN]: "between",
};
